/**
 * Dining (café / restaurant) shapes. Mirrors backend modules/dining.
 */
export type DiningBookingMode = 'TABLE_RESERVATION' | 'ENTRY_PASS' | 'EVENT';
export type DiningPriceUnit = 'FLAT' | 'PER_GUEST';
export type DiningKind = 'PET_CAFE' | 'PET_RESTAURANT' | 'PET_BAKERY';

export type DiningProfile = {
  kind: DiningKind;
  dine_in: boolean;
  delivery: boolean;
  pickup: boolean;
  walk_in: boolean;
  opening_time: string; // HH:mm
  closing_time: string;
};

export type DiningProfileUpdate = Partial<DiningProfile>;

import type { CatalogImage } from '@/features/catalog-image/types';

export type DiningOffering = {
  id: string;
  service_id: string;
  mode: DiningBookingMode;
  name: string;
  description: string | null;
  price_inr: string; // decimal string
  price_unit: DiningPriceUnit;
  is_active: boolean;
  min_party: number;
  max_party: number;
  turn_minutes: number | null;
  capacity: number;
  /** Booking amount is deducted from the bill at the venue (client change, 9 Oct 2026). */
  redeemable_against_bill: boolean;
  images: CatalogImage[];
  event_start: string | null;
  event_end: string | null;
  created_at: string;
  updated_at: string;
};

/** Public list adds remaining tickets for EVENT offerings (null otherwise). */
export type PublicOffering = DiningOffering & { remaining: number | null };

export type CreateOfferingInput = {
  name: string;
  description?: string;
  mode: DiningBookingMode;
  price_inr: number;
  price_unit: DiningPriceUnit;
  min_party: number;
  max_party: number;
  turn_minutes?: number;
  capacity: number;
  event_start?: string;
  event_end?: string;
  is_active?: boolean;
  redeemable_against_bill?: boolean;
};

export type UpdateOfferingInput = Partial<Omit<CreateOfferingInput, 'mode'>>;

export type OfferingSlot = { slot_start: string; slot_end: string; remaining: number };
export type OfferingAvailability = {
  service_id: string;
  mode: DiningBookingMode;
  date: string;
  slots: OfferingSlot[];
};

export const MODE_LABEL: Record<DiningBookingMode, string> = {
  TABLE_RESERVATION: 'Table reservation',
  ENTRY_PASS: 'Entry pass',
  EVENT: 'Event',
};

export const KIND_LABEL: Record<DiningKind, string> = {
  PET_CAFE: 'Pet Café',
  PET_RESTAURANT: 'Pet Restaurant',
  PET_BAKERY: 'Pet Bakery',
};
